import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import AlertToast from '../../../components/common/AlertToast';

const DEFAULT_CLASSES = [
  'Pre-Primary (Nursery)', 'Pre-Primary (LKG)', 'Pre-Primary (UKG)',
  'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
  'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10',
  'Class 11 (Science)', 'Class 11 (Commerce)', 'Class 11 (Arts)',
  'Class 12 (Science)', 'Class 12 (Commerce)', 'Class 12 (Arts)',
];
const DEFAULT_SUBJECTS = [
  'Mathematics', 'Science', 'English', 'Social Studies', 'Hindi', 'Tamil',
  'Computer Science', 'Physics', 'Chemistry', 'Biology', 'History',
  'Geography', 'Economics', 'Accountancy', 'Business Studies', 'Physical Education',
];
const DEFAULT_SECTIONS = ['A', 'B', 'C', 'D', 'E'];
const NOTE_TYPES = ['Chapter Notes', 'Summary', 'Mind Map', 'Practice Questions', 'Revision Sheet', 'Formula Sheet', 'Glossary', 'Other'];
const NOTE_TYPE_COLORS = {
  'Chapter Notes': '#6366f1', 'Summary': '#0ea5e9', 'Mind Map': '#8b5cf6',
  'Practice Questions': '#ef4444', 'Revision Sheet': '#f59e0b',
  'Formula Sheet': '#22c55e', 'Glossary': '#64748b', 'Other': '#94a3b8',
};
const CONTENT_TYPES = [
  { value: 'text',       label: 'Text / Notes',  icon: 'bi-file-text',          color: '#6366f1' },
  { value: 'video',      label: 'Video Link',     icon: 'bi-play-btn-fill',      color: '#ef4444' },
  { value: 'link',       label: 'External Link',  icon: 'bi-link-45deg',         color: '#0ea5e9' },
  { value: 'pdf',        label: 'PDF / File',     icon: 'bi-file-earmark-pdf',   color: '#f59e0b' },
  { value: 'ai',         label: 'AI Generated',   icon: 'bi-robot',              color: '#8b5cf6' },
  { value: 'assignment', label: 'Assignment',     icon: 'bi-pencil-square',      color: '#22c55e' },
];

const uid = () => `${Date.now()}-${Math.floor(Math.random() * 9999)}`;
const blankChapter = (order) => ({ id: uid(), title: '', order, topics: [] });
const blankTopic   = ()       => ({ id: uid(), title: '', isCompleted: false, completedBy: null, completedAt: null });
const blankContent = { title: '', content_type: 'text', content: '', url: '', chapter_title: '', topic_title: '', tags: '', ai_generated: false };
const blankNote    = { title: '', class_grade: '', section: '', subject: '', chapter: '', note_type: 'Chapter Notes', content: '', tags: '' };

export default function StudentCoursePage() {
  const { user } = useAuth();
  const isStaff = ['admin', 'manager', 'teacher', 'faculty', 'principal', 'staff'].includes(user?.roleKey);
  const isAdmin = ['admin', 'manager', 'principal'].includes(user?.roleKey);

  const fileInputRef    = useRef(null);
  const contentFileRef  = useRef(null);
  const noteFileRef     = useRef(null);

  // School config
  const [classOptions,   setClassOptions]   = useState(DEFAULT_CLASSES);
  const [sectionOptions, setSectionOptions] = useState(DEFAULT_SECTIONS);
  const [subjectOptions, setSubjectOptions] = useState(DEFAULT_SUBJECTS);

  // Global filter
  const [selClass,   setSelClass]   = useState('');
  const [selSection, setSelSection] = useState('');
  const [selSubject, setSelSubject] = useState('');

  const [activeTab, setActiveTab] = useState('content');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const showToast = (message, type = 'success') => setToast({ show: true, message, type });

  // ── AI GENERATOR STATE ──────────────────────────────────────────
  const [aiContentStyle,   setAiContentStyle]   = useState('');
  const [aiTone,           setAiTone]           = useState('standard');

  // ── CONTENT STATE ───────────────────────────────────────────────
  const [contents,       setContents]       = useState([]);
  const [selChapter,     setSelChapter]     = useState('');
  const [selTopic,       setSelTopic]       = useState('');
  const [showContentForm,setShowContentForm]= useState(false);
  const [contentForm,    setContentForm]    = useState(blankContent);
  const [editingContent, setEditingContent] = useState(null);
  const [contentSaving,  setContentSaving]  = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [showAiPanel,    setShowAiPanel]    = useState(false);
  const [aiPrompt,       setAiPrompt]       = useState('');
  const [aiGenerating,   setAiGenerating]   = useState(false);
  const [aiResult,       setAiResult]       = useState('');
  const [pdfParsing,     setPdfParsing]     = useState(false);

  // ── NOTES STATE ─────────────────────────────────────────────────
  const [notes,          setNotes]          = useState([]);
  const [noteTotal,      setNoteTotal]      = useState(0);
  const [notePage,       setNotePage]       = useState(1);
  const [notesLoading,   setNotesLoading]   = useState(false);
  const [noteForm,       setNoteForm]       = useState(blankNote);
  const [editingNote,    setEditingNote]    = useState(null);
  const [showNoteForm,   setShowNoteForm]   = useState(false);
  const [noteSaving,     setNoteSaving]     = useState(false);
  const [viewNote,       setViewNote]       = useState(null);
  const [showNoteAi,     setShowNoteAi]     = useState(false);
  const [noteAiPrompt,   setNoteAiPrompt]   = useState('');
  const [noteAiGenerating,setNoteAiGenerating]=useState(false);
  const [noteFilterClass,setNoteFilterClass]= useState('');
  const [noteFilterSub,  setNoteFilterSub]  = useState('');
  const [noteFilterType, setNoteFilterType] = useState('');
  const [noteSearch,     setNoteSearch]     = useState('');
  const [bookmarks,      setBookmarks]      = useState(() => {
    try { return JSON.parse(localStorage.getItem('tslms_note_bookmarks') || '[]'); } catch { return []; }
  });

  // ── LOAD SCHOOL CONFIG ──────────────────────────────────────────
  useEffect(() => {
    api.get('/records/school_config', { params: { limit: 1 } }).then(r => {
      const rd = r.data?.records?.[0]?.recordData;
      if (rd) {
        if (Array.isArray(rd.classes)  && rd.classes.length)  setClassOptions(rd.classes);
        if (Array.isArray(rd.sections) && rd.sections.length) setSectionOptions(rd.sections);
        if (Array.isArray(rd.subjects) && rd.subjects.length) setSubjectOptions(rd.subjects);
      }
    }).catch(() => {});
  }, []);


  // ── LOAD CONTENT ────────────────────────────────────────────────
  const loadContents = useCallback(async () => {
    if (!selClass || !selSection || !selSubject) { setContents([]); return; }
    setContentLoading(true);
    const params = { class_grade: selClass, section: selSection, subject: selSubject };
    if (selChapter) params.chapter_title = selChapter;
    if (selTopic)   params.topic_title   = selTopic;
    const r = await api.get('/education/content', { params }).catch(() => ({ data: { records: [] } }));
    setContents(r.data.records || []);
    setContentLoading(false);
  }, [selClass, selSection, selSubject, selChapter, selTopic]);

  useEffect(() => { if (activeTab === 'content') loadContents(); }, [loadContents, activeTab]);

  // ── LOAD NOTES ──────────────────────────────────────────────────
  const loadNotes = useCallback(async () => {
    setNotesLoading(true);
    const params = { page: notePage, limit: 20 };
    if (noteFilterClass) params.class_grade = noteFilterClass;
    if (noteFilterSub)   params.subject     = noteFilterSub;
    if (noteFilterType)  params.note_type   = noteFilterType;
    if (noteSearch)      params.search      = noteSearch;
    const r = await api.get('/education/notes', { params }).catch(() => ({ data: { records: [], total: 0 } }));
    setNotes(r.data.records || []);
    setNoteTotal(r.data.total || 0);
    setNotesLoading(false);
  }, [notePage, noteFilterClass, noteFilterSub, noteFilterType, noteSearch]);

  useEffect(() => { if (activeTab === 'notes') loadNotes(); }, [loadNotes, activeTab]);

  // ── AI HELPERS ──────────────────────────────────────────────────
  const getAgeGroup = (cls) => {
    const n = parseInt((cls || '').replace(/\D/g, '')) || 0;
    if (n <= 5 || cls?.toLowerCase().includes('pre') || cls?.toLowerCase().includes('lkg') || cls?.toLowerCase().includes('ukg')) return 'primary';
    if (n <= 8) return 'middle';
    if (n <= 10) return 'secondary';
    return 'senior';
  };

  const AI_STYLES = [
    { key: 'story',       label: 'Story / Narrative',     icon: 'bi-book-half',           color: '#f59e0b', groups: ['primary','middle'] },
    { key: 'concept',     label: 'Concept Explanation',   icon: 'bi-lightbulb-fill',       color: '#6366f1', groups: ['primary','middle','secondary','senior'] },
    { key: 'activity',    label: 'Activity Sheet',        icon: 'bi-puzzle-fill',          color: '#22c55e', groups: ['primary','middle'] },
    { key: 'image_desc',  label: 'Image / Diagram Idea',  icon: 'bi-image-fill',           color: '#0ea5e9', groups: ['primary','middle','secondary'] },
    { key: 'summary',     label: 'Summary Notes',         icon: 'bi-card-text',            color: '#8b5cf6', groups: ['middle','secondary','senior'] },
    { key: 'worksheet',   label: 'Worksheet / Exercises', icon: 'bi-pencil-square',        color: '#ef4444', groups: ['middle','secondary','senior'] },
    { key: 'mcq',         label: 'MCQ Question Bank',     icon: 'bi-check2-square',        color: '#f97316', groups: ['middle','secondary','senior'] },
    { key: 'mindmap',     label: 'Mind Map Outline',      icon: 'bi-diagram-3-fill',       color: '#10b981', groups: ['middle','secondary','senior'] },
    { key: 'flashcards',  label: 'Flashcard Set',         icon: 'bi-collection-fill',      color: '#6366f1', groups: ['primary','middle','secondary'] },
    { key: 'case_study',  label: 'Case Study',            icon: 'bi-file-earmark-text-fill',color: '#64748b', groups: ['secondary','senior'] },
  ];

  const buildAiPrompt = (style, cls, section, subject, chapter, topic, tone) => {
    const grp = getAgeGroup(cls);
    const ctx = [cls, section && `Section ${section}`, subject, chapter, topic].filter(Boolean).join(' › ');
    const toneNote = tone === 'fun' ? 'Make it fun, engaging, and easy to understand for young learners.'
                   : tone === 'advanced' ? 'Use academic language appropriate for board exam preparation.'
                   : 'Use clear, structured language appropriate for school students.';

    const templates = {
      story: `Write a short educational story (300-400 words) that teaches the concept of "${topic || chapter || subject}" for ${cls} students.\n\nContext: ${ctx}\n\nRequirements:\n- Use relatable characters and a simple plot\n- Embed the concept naturally within the story\n- End with a moral or key learning\n- ${toneNote}`,
      concept: `Create a detailed concept explanation for "${topic || chapter || subject}" for ${cls} students.\n\nContext: ${ctx}\n\nInclude:\n1. Simple definition (2-3 lines)\n2. Key points (5-7 bullet points)\n3. Real-life example with analogy\n4. Important terms with definitions\n5. Common mistakes students make\n6. Quick recap\n\n${toneNote}`,
      activity: `Design a hands-on classroom activity for teaching "${topic || chapter || subject}" to ${cls} students.\n\nContext: ${ctx}\n\nInclude:\n- Objective\n- Materials needed\n- Step-by-step instructions (5-6 steps)\n- Discussion questions (3)\n- Learning outcome\n\n${toneNote}`,
      image_desc: `Describe 3 educational diagrams or images that a teacher should draw/display to explain "${topic || chapter || subject}" for ${cls} students.\n\nContext: ${ctx}\n\nFor each image describe:\n- What to draw/show\n- Labels to include\n- Color suggestions\n- What concept it illustrates\n\n${toneNote}`,
      summary: `Create comprehensive summary notes for "${topic || chapter || subject}" for ${cls} students.\n\nContext: ${ctx}\n\nStructure:\n# ${topic || chapter || subject}\n\n## Key Concepts\n[list]\n\n## Important Definitions\n[terms]\n\n## Formulas / Rules\n[if applicable]\n\n## Examples\n[worked examples]\n\n## Remember This\n[memory tips]\n\n${toneNote}`,
      worksheet: `Create a practice worksheet for "${topic || chapter || subject}" for ${cls} students.\n\nContext: ${ctx}\n\nInclude:\n- Section A: Fill in the blanks (5 questions)\n- Section B: Short answer (3 questions, 2 marks each)\n- Section C: Long answer (1 question, 5 marks)\n- Section D: Draw and label (if applicable)\n\nProvide answer key at the end.\n\n${toneNote}`,
      mcq: `Create 10 multiple choice questions on "${topic || chapter || subject}" for ${cls} students.\n\nContext: ${ctx}\n\nRequirements:\n- Mix of easy (3), medium (5), and hard (2) difficulty\n- 4 options each (A, B, C, D)\n- One clearly correct answer\n- Brief explanation for each answer\n- Cover different aspects of the topic\n\n${toneNote}`,
      mindmap: `Create a detailed mind map outline for "${topic || chapter || subject}" for ${cls} students.\n\nContext: ${ctx}\n\nStructure as:\nCENTRAL TOPIC: ${topic || chapter || subject}\n  ├── Branch 1: [main concept]\n  │     ├── Sub-point 1a\n  │     └── Sub-point 1b\n  ├── Branch 2: [main concept]\n  ...\n\nInclude 5-6 main branches with 3-4 sub-points each.\n\n${toneNote}`,
      flashcards: `Create 12 flashcards for "${topic || chapter || subject}" for ${cls} students.\n\nContext: ${ctx}\n\nFormat each as:\n🃏 Card [N]\nFRONT: [Term / Question]\nBACK: [Definition / Answer]\n\nCover key vocabulary, important dates/numbers, and concept questions.\n\n${toneNote}`,
      case_study: `Write a detailed case study on "${topic || chapter || subject}" for ${cls} students.\n\nContext: ${ctx}\n\nStructure:\n1. Background / Introduction\n2. The Situation / Problem\n3. Key Facts\n4. Analysis (apply concepts from ${subject})\n5. Discussion Questions (4)\n6. Conclusion / Learning Points\n\n${toneNote}`,
    };
    return templates[style] || `Generate educational content on "${topic || chapter || subject}" for ${cls} students studying ${subject}.\n\n${toneNote}`;
  };

  // ── CONTENT HELPERS ─────────────────────────────────────────────
  const cf = (k, v) => setContentForm(p => ({ ...p, [k]: v }));

  const saveContent = async (e) => {
    e.preventDefault(); setContentSaving(true);
    try {
      const data = { ...contentForm, class_grade: selClass, section: selSection, subject: selSubject };
      if (editingContent?._id) await api.put(`/education/content/${editingContent._id}`, data);
      else                      await api.post('/education/content', data);
      showToast('Content saved!');
      setShowContentForm(false); setEditingContent(null); setContentForm(blankContent); loadContents();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error saving', 'danger');
    }
    setContentSaving(false);
  };

  const deleteContent = async (id) => {
    if (!window.confirm('Delete this content?')) return;
    await api.delete(`/education/content/${id}`).catch(() => {});
    loadContents();
  };


  const generateContentAI = async () => {
    const prompt = aiPrompt.trim() || buildAiPrompt(aiContentStyle, selClass, selSection, selSubject, contentForm.chapter_title || selChapter, contentForm.topic_title || selTopic, aiTone);
    if (!prompt) { showToast('Please select a content style or write a prompt', 'warning'); return; }
    setAiGenerating(true);
    try {
      const r = await api.post('/education/notes/generate', {
        prompt, class_grade: selClass, subject: selSubject, section: selSection,
        chapter: contentForm.chapter_title || selChapter,
        topic: contentForm.topic_title || selTopic,
        content_style: aiContentStyle, tone: aiTone,
      });
      const generated = r.data?.data?.content || r.data?.content || r.data?.text || buildAiPrompt(aiContentStyle, selClass, selSection, selSubject, contentForm.chapter_title || selChapter, contentForm.topic_title, aiTone);
      setAiResult(generated);
      const title = `${aiContentStyle ? AI_STYLES.find(s => s.key === aiContentStyle)?.label + ' — ' : ''}${contentForm.chapter_title || selChapter || selSubject}`;
      setContentForm(f => ({ ...f, content: generated, ai_generated: true, content_type: 'ai', title: f.title || title }));
      showToast('Content generated! Review and save.');
    } catch {
      const tpl = buildAiPrompt(aiContentStyle, selClass, selSection, selSubject, contentForm.chapter_title || selChapter, contentForm.topic_title, aiTone);
      setAiResult(tpl);
      setContentForm(f => ({ ...f, content: tpl, ai_generated: true, content_type: 'ai' }));
      showToast('Template generated — customize and save.', 'info');
    }
    setAiGenerating(false);
  };

  const handleContentFileImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPdfParsing(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/education/notes/parse-pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setContentForm(f => ({ ...f, content: r.data?.text || r.data?.content || '', title: f.title || file.name.replace(/\.[^.]+$/, '') }));
      showToast('Content imported!');
    } catch {
      setContentForm(f => ({ ...f, title: f.title || file.name.replace(/\.[^.]+$/, ''), content: f.content + `\n\n[Imported: ${file.name}]` }));
      showToast('File uploaded. Please paste or type the content.', 'warning');
    }
    setPdfParsing(false);
  };

  // ── NOTE HELPERS ────────────────────────────────────────────────
  const nf = (k, v) => setNoteForm(p => ({ ...p, [k]: v }));

  const saveNote = async (e) => {
    e.preventDefault(); setNoteSaving(true);
    try {
      if (editingNote?._id) await api.put(`/education/notes/${editingNote._id}`, noteForm);
      else                   await api.post('/education/notes', noteForm);
      showToast(`Note ${editingNote ? 'updated' : 'saved'}!`);
      setShowNoteForm(false); setEditingNote(null); setNoteForm(blankNote); loadNotes();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error saving note', 'danger');
    }
    setNoteSaving(false);
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    await api.delete(`/education/notes/${id}`).catch(() => {});
    loadNotes();
  };

  const generateNoteAI = async () => {
    if (!noteAiPrompt.trim()) return;
    setNoteAiGenerating(true);
    try {
      const r = await api.post('/education/notes/generate', {
        prompt: noteAiPrompt,
        class_grade: noteForm.class_grade || selClass,
        subject:     noteForm.subject     || selSubject,
        chapter:     noteForm.chapter,
      });
      const generated = r.data?.data?.content || r.data?.content || r.data?.text || '';
      setNoteForm(f => ({ ...f, content: generated }));
      setShowNoteAi(false); setShowNoteForm(true);
      showToast('Note generated! Review and save.');
    } catch {
      const tpl = `# ${noteForm.chapter || 'Chapter'}\n\n## Key Points\n- Point 1\n- Point 2\n\n## Summary\n[Write summary here]`;
      setNoteForm(f => ({ ...f, content: tpl }));
      setShowNoteAi(false); setShowNoteForm(true);
    }
    setNoteAiGenerating(false);
  };

  const toggleBookmark = (id) => {
    const updated = bookmarks.includes(id) ? bookmarks.filter(b => b !== id) : [...bookmarks, id];
    setBookmarks(updated);
    localStorage.setItem('tslms_note_bookmarks', JSON.stringify(updated));
  };

  const duplicateNote = async (note) => {
    const d = note.recordData || {};
    await api.post('/education/notes', { ...d, title: `${d.title} (Copy)` }).catch(() => {});
    showToast('Note duplicated!');
    loadNotes();
  };

  const downloadNote = (note) => {
    const d = note.recordData || {};
    const text = `${d.title}\n${'='.repeat((d.title || '').length || 20)}\nClass: ${d.class_grade || ''} | Section: ${d.section || ''} | Subject: ${d.subject || ''}\nChapter: ${d.chapter || ''} | Type: ${d.note_type || ''}\n\n${d.content || ''}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${d.title || 'note'}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClass = async (note, targetClass) => {
    const d = note.recordData || {};
    await api.post('/education/notes', { ...d, class_grade: targetClass, title: `${d.title} (${targetClass})` }).catch(() => {});
    showToast(`Copied to ${targetClass}!`);
    loadNotes();
  };

  const selectionMade = selClass && selSection && selSubject;

  // ── RENDER ──────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h4 className="mb-0 fw-bold" style={{ color: '#0f172a' }}>
            <i className="bi bi-collection me-2 text-primary"></i>Course Content &amp; Notes
          </h4>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            Create, manage and AI-generate subject-wise content and reusable study notes
          </div>
        </div>
      </div>

      {/* Selection Bar */}
      <div className="card mb-3" style={{ borderRadius: 12, border: '2px solid #e0e7ff' }}>
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: '#6366f1' }}>
                <i className="bi bi-mortarboard me-1"></i>Class / Standard
              </label>
              <select className="form-select" value={selClass} onChange={e => { setSelClass(e.target.value); setSelChapter(''); setSelTopic(''); }}>
                <option value="">Select Class...</option>
                {classOptions.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: '#f59e0b' }}>
                <i className="bi bi-grid-3x3-gap me-1"></i>Section
              </label>
              <select className="form-select" value={selSection} onChange={e => setSelSection(e.target.value)}>
                <option value="">Select Section...</option>
                {sectionOptions.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: '#22c55e' }}>
                <i className="bi bi-book me-1"></i>Subject
              </label>
              <select className="form-select" value={selSubject} onChange={e => setSelSubject(e.target.value)}>
                <option value="">Select Subject...</option>
                {subjectOptions.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-md-3 text-end">
              {selectionMade && (
                <div>
                  <span className="badge" style={{ background: '#ede9fe', color: '#6366f1', fontSize: 12, padding: '6px 12px' }}>
                    <i className="bi bi-check-circle me-1"></i>{selClass} · Sec {selSection} · {selSubject}
                  </span>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                    <i className="bi bi-cpu me-1"></i>AI-ready context loaded
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {[
          { key: 'content', label: 'Course Content', icon: 'bi-collection-fill' },
          { key: 'notes',   label: 'Reusable Notes', icon: 'bi-journal-richtext' },
        ].map(t => (
          <li className="nav-item" key={t.key}>
            <button className={`nav-link ${activeTab === t.key ? 'active fw-semibold' : ''}`} onClick={() => setActiveTab(t.key)}>
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ── COURSE CONTENT ────────────────────────────────────────── */}
      {activeTab === 'content' && (
        !selectionMade ? (
          <div className="card text-center py-5 text-muted">
            <i className="bi bi-arrow-up-circle display-4 d-block mb-2 opacity-25"></i>
            <div>Select a Class, Section, and Subject above to manage course content</div>
          </div>
        ) : (
          <div className="row g-3">
            {/* Left: chapter/topic filter */}
            <div className="col-lg-3">
              <div className="card" style={{ borderRadius: 10 }}>
                <div className="card-header fw-semibold" style={{ fontSize: 13 }}>
                  <i className="bi bi-funnel me-2 text-primary"></i>Filter Content
                </div>
                <div className="card-body p-2">
                  <label className="form-label mb-1" style={{ fontSize: 11, color: '#64748b' }}>Chapter</label>
                  <input className="form-control form-control-sm mb-2" placeholder="e.g. Chapter 3..." value={selChapter}
                    onChange={e => { setSelChapter(e.target.value); setSelTopic(''); }} />
                  <label className="form-label mb-1" style={{ fontSize: 11, color: '#64748b' }}>Topic</label>
                  <input className="form-control form-control-sm" placeholder="e.g. Photosynthesis..." value={selTopic}
                    onChange={e => setSelTopic(e.target.value)} />
                  {(selChapter || selTopic) && (
                    <button className="btn btn-link btn-sm p-0 mt-2" style={{ fontSize: 11 }} onClick={() => { setSelChapter(''); setSelTopic(''); }}>
                      <i className="bi bi-x-circle me-1"></i>Clear filters
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Right: content list + forms */}
            <div className="col-lg-9">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span style={{ fontSize: 13, color: '#64748b' }}>
                  {contentLoading ? 'Loading…' : `${contents.length} item(s)`}
                  {selChapter && <span className="ms-2 badge bg-primary bg-opacity-10 text-primary">{selChapter}</span>}
                  {selTopic   && <span className="ms-1 badge bg-success bg-opacity-10 text-success">{selTopic}</span>}
                </span>
                {isStaff && (
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm" style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', fontWeight: 600 }}
                      onClick={() => { setShowAiPanel(p => !p); setAiResult(''); setAiContentStyle(''); }}>
                      <i className="bi bi-robot me-1"></i>AI Generate
                    </button>
                    <button className="btn btn-outline-warning btn-sm" onClick={() => contentFileRef.current?.click()} disabled={pdfParsing}>
                      {pdfParsing ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-upload me-1"></i>Import</>}
                    </button>
                    <input type="file" ref={contentFileRef} accept=".pdf,.doc,.docx,.txt" style={{ display: 'none' }} onChange={handleContentFileImport} />
                    <button className="btn btn-primary btn-sm" onClick={() => {
                      setContentForm({ ...blankContent, chapter_title: selChapter, topic_title: selTopic });
                      setEditingContent(null); setShowContentForm(true);
                    }}>
                      <i className="bi bi-plus-lg me-1"></i>Add Content
                    </button>
                  </div>
                )}
              </div>

              {/* ── AI Panel ─────────────────────────────────────── */}
              {showAiPanel && (() => {
                const ageGroup = getAgeGroup(selClass);
                const relevantStyles = AI_STYLES.filter(s => s.groups.includes(ageGroup));
                return (
                  <div className="card mb-3" style={{ border: '2px solid #ede9fe', borderRadius: 12 }}>
                    <div className="card-header d-flex align-items-center justify-content-between py-2"
                      style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', borderRadius: '10px 10px 0 0' }}>
                      <span className="fw-bold" style={{ fontSize: 14 }}>
                        <i className="bi bi-robot me-2"></i>AI Content Generator
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.85, marginLeft: 8 }}>
                          {selClass} · Sec {selSection} · {selSubject}
                        </span>
                      </span>
                      <button type="button" className="btn-close btn-close-white btn-sm" onClick={() => setShowAiPanel(false)}></button>
                    </div>

                    <div className="card-body" style={{ background: '#fafbff' }}>
                      {/* Content Style Chips */}
                      <div className="mb-3">
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                          Pick what to generate:
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {relevantStyles.map(s => (
                            <button key={s.key} type="button"
                              onClick={() => setAiContentStyle(s.key)}
                              style={{
                                padding: '5px 12px', borderRadius: 20, fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
                                border: `2px solid ${aiContentStyle === s.key ? s.color : '#e2e8f0'}`,
                                background: aiContentStyle === s.key ? `${s.color}18` : '#fff',
                                color: aiContentStyle === s.key ? s.color : '#374151',
                              }}>
                              <i className={`bi ${s.icon} me-1`}></i>{s.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Chapter / Topic */}
                      <div className="row g-2 mb-3">
                        <div className="col-md-6">
                          <input className="form-control form-control-sm" placeholder="Chapter (e.g. Chapter 3 — Cells)"
                            value={contentForm.chapter_title} onChange={e => cf('chapter_title', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <input className="form-control form-control-sm" placeholder="Topic (optional, e.g. Mitosis)"
                            value={contentForm.topic_title} onChange={e => cf('topic_title', e.target.value)} />
                        </div>
                      </div>

                      {/* Generate Button */}
                      <button className="btn w-100 fw-bold" onClick={generateContentAI}
                        disabled={aiGenerating || !aiContentStyle}
                        style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', borderRadius: 10, padding: '10px', fontSize: 14 }}>
                        {aiGenerating
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>Generating…</>
                          : <><i className="bi bi-stars me-2"></i>{aiContentStyle ? `Generate ${AI_STYLES.find(s=>s.key===aiContentStyle)?.label}` : 'Select a style above'}</>
                        }
                      </button>

                      {/* Result */}
                      {aiResult && (
                        <div className="mt-3">
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#15803d' }}>
                              <i className="bi bi-check-circle-fill me-1"></i>Generated — review and save
                            </span>
                            <button className="btn btn-link btn-sm p-0" style={{ fontSize: 11, color: '#94a3b8' }} onClick={() => { setAiResult(''); setAiContentStyle(''); }}>
                              Clear
                            </button>
                          </div>
                          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, maxHeight: 260, overflowY: 'auto', fontSize: 12.5, lineHeight: 1.8, whiteSpace: 'pre-wrap', color: '#1e293b' }}>
                            {aiResult}
                          </div>
                          <button className="btn btn-success w-100 mt-2 fw-semibold" style={{ borderRadius: 8 }}
                            onClick={() => { setShowContentForm(true); setShowAiPanel(false); }}>
                            <i className="bi bi-pencil-square me-1"></i>Edit &amp; Save This Content
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Content Form */}
              {showContentForm && (
                <div className="card mb-3" style={{ border: '2px solid #e0e7ff', borderRadius: 10 }}>
                  <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#f5f8ff' }}>
                    <span className="fw-semibold" style={{ fontSize: 13 }}><i className="bi bi-pencil me-2 text-primary"></i>{editingContent ? 'Edit Content' : 'Add New Content'}</span>
                    <button type="button" className="btn-close btn-sm" onClick={() => { setShowContentForm(false); setEditingContent(null); setContentForm(blankContent); }}></button>
                  </div>
                  <div className="card-body">
                    <form onSubmit={saveContent}>
                      <div className="row g-2">
                        <div className="col-md-6">
                          <label className="form-label" style={{ fontSize: 12 }}>Title <span className="text-danger">*</span></label>
                          <input className="form-control form-control-sm" required value={contentForm.title} onChange={e => cf('title', e.target.value)} placeholder="Content title..." />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label" style={{ fontSize: 12 }}>Type</label>
                          <select className="form-select form-select-sm" value={contentForm.content_type} onChange={e => cf('content_type', e.target.value)}>
                            {CONTENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label" style={{ fontSize: 12 }}>Chapter</label>
                          <input className="form-control form-control-sm" placeholder="e.g. Chapter 3 — Cells..."
                            value={contentForm.chapter_title} onChange={e => cf('chapter_title', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label" style={{ fontSize: 12 }}>Topic (optional)</label>
                          <input className="form-control form-control-sm" placeholder="Topic name..." value={contentForm.topic_title} onChange={e => cf('topic_title', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label" style={{ fontSize: 12 }}>Tags (comma separated)</label>
                          <input className="form-control form-control-sm" placeholder="e.g. important, exam..." value={contentForm.tags} onChange={e => cf('tags', e.target.value)} />
                        </div>
                        {(contentForm.content_type === 'video' || contentForm.content_type === 'link') && (
                          <div className="col-12">
                            <label className="form-label" style={{ fontSize: 12 }}>URL <span className="text-danger">*</span></label>
                            <input type="url" className="form-control form-control-sm" required value={contentForm.url} onChange={e => cf('url', e.target.value)} placeholder="https://..." />
                          </div>
                        )}
                        {contentForm.content_type === 'pdf' && (
                          <div className="col-12">
                            <label className="form-label" style={{ fontSize: 12 }}>File URL</label>
                            <input className="form-control form-control-sm" value={contentForm.url} onChange={e => cf('url', e.target.value)} placeholder="Paste file URL..." />
                          </div>
                        )}
                        {['text', 'ai', 'assignment'].includes(contentForm.content_type) && (
                          <div className="col-12">
                            <label className="form-label" style={{ fontSize: 12 }}>Content / Notes</label>
                            <textarea className="form-control form-control-sm" rows={10} value={contentForm.content} onChange={e => cf('content', e.target.value)}
                              placeholder="Type content here... Supports markdown-style formatting" />
                          </div>
                        )}
                        <div className="col-12 d-flex gap-2 justify-content-end pt-1 border-top">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowContentForm(false); setEditingContent(null); }}>Cancel</button>
                          <button type="submit" className="btn btn-primary btn-sm" disabled={contentSaving}>
                            {contentSaving ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-check2 me-1"></i>}
                            {editingContent ? 'Update' : 'Save Content'}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Content List */}
              {contentLoading ? (
                <div className="text-center py-4"><span className="spinner-border text-primary"></span></div>
              ) : contents.length === 0 ? (
                <div className="card text-center py-5 text-muted">
                  <i className="bi bi-collection display-4 d-block mb-2 opacity-25"></i>
                  <div>No content yet.{isStaff && ' Click "Add Content" or use AI to create content.'}</div>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {contents.map(item => {
                    const d  = item.recordData || {};
                    const ct = CONTENT_TYPES.find(t => t.value === d.content_type) || CONTENT_TYPES[0];
                    return (
                      <div key={item._id} className="card" style={{ borderRadius: 10, borderLeft: `4px solid ${ct.color}` }}>
                        <div className="card-body py-2 px-3">
                          <div className="d-flex align-items-start gap-2">
                            <i className={`bi ${ct.icon} mt-1`} style={{ color: ct.color, fontSize: 18 }}></i>
                            <div className="flex-grow-1">
                              <div style={{ fontWeight: 700, fontSize: 14 }}>{d.title || 'Untitled'}</div>
                              <div className="d-flex flex-wrap gap-1 mt-1">
                                <span className="badge" style={{ background: `${ct.color}15`, color: ct.color, fontSize: 10 }}>{ct.label}</span>
                                {d.chapter_title && <span className="badge bg-light text-muted" style={{ fontSize: 10 }}>{d.chapter_title}</span>}
                                {d.topic_title   && <span className="badge bg-light text-muted" style={{ fontSize: 10 }}>{d.topic_title}</span>}
                                {d.ai_generated  && <span className="badge" style={{ background: '#f5f3ff', color: '#8b5cf6', fontSize: 10 }}><i className="bi bi-robot me-1"></i>AI</span>}
                              </div>
                              {d.content && (
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                  {d.content.substring(0, 160)}
                                </div>
                              )}
                              {d.url && (
                                <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#6366f1' }}>
                                  <i className="bi bi-box-arrow-up-right me-1"></i>Open Link
                                </a>
                              )}
                              {d.tags && (
                                <div className="mt-1 d-flex flex-wrap gap-1">
                                  {d.tags.split(',').map(tag => <span key={tag.trim()} className="badge bg-secondary bg-opacity-25 text-secondary" style={{ fontSize: 10 }}>#{tag.trim()}</span>)}
                                </div>
                              )}
                            </div>
                            {isStaff && (
                              <div className="d-flex gap-1 ms-2">
                                <button className="btn btn-outline-primary btn-sm" style={{ padding: '1px 5px', fontSize: 11 }}
                                  onClick={() => { setContentForm({ ...blankContent, ...d }); setEditingContent(item); setShowContentForm(true); }}>
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <button className="btn btn-outline-danger btn-sm" style={{ padding: '1px 5px', fontSize: 11 }}
                                  onClick={() => deleteContent(item._id)}>
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* ── REUSABLE NOTES ────────────────────────────────────────── */}
      {activeTab === 'notes' && (
        <>
          {/* Filter bar */}
          <div className="card mb-3">
            <div className="card-body py-2">
              <div className="row g-2 align-items-center">
                <div className="col-md-3">
                  <input type="text" className="form-control form-control-sm" placeholder="Search notes…"
                    value={noteSearch} onChange={e => { setNoteSearch(e.target.value); setNotePage(1); }} />
                </div>
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={noteFilterClass} onChange={e => { setNoteFilterClass(e.target.value); setNotePage(1); }}>
                    <option value="">All Classes</option>
                    {classOptions.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={noteFilterSub} onChange={e => { setNoteFilterSub(e.target.value); setNotePage(1); }}>
                    <option value="">All Subjects</option>
                    {subjectOptions.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={noteFilterType} onChange={e => { setNoteFilterType(e.target.value); setNotePage(1); }}>
                    <option value="">All Types</option>
                    {NOTE_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-md-3 d-flex gap-2 justify-content-end">
                  {isStaff && (
                    <>
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => { setShowNoteAi(p => !p); setShowNoteForm(false); }}>
                        <i className="bi bi-robot me-1"></i>AI
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={() => {
                        setNoteForm({ ...blankNote, class_grade: selClass, section: selSection, subject: selSubject });
                        setEditingNote(null); setShowNoteForm(true); setShowNoteAi(false); setViewNote(null);
                      }}>
                        <i className="bi bi-plus-lg me-1"></i>New Note
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* AI Note Generator */}
          {showNoteAi && (
            <div className="card mb-3" style={{ border: '2px solid #ede9fe', borderRadius: 10 }}>
              <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#f5f3ff' }}>
                <span className="fw-semibold" style={{ fontSize: 13 }}><i className="bi bi-robot me-2 text-primary"></i>AI Note Generator</span>
                <button type="button" className="btn-close btn-sm" onClick={() => setShowNoteAi(false)}></button>
              </div>
              <div className="card-body">
                <div className="row g-2 mb-2">
                  <div className="col-md-3">
                    <label className="form-label" style={{ fontSize: 12 }}>Class</label>
                    <select className="form-select form-select-sm" value={noteForm.class_grade} onChange={e => nf('class_grade', e.target.value)}>
                      <option value="">Select</option>
                      {classOptions.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label" style={{ fontSize: 12 }}>Subject</label>
                    <select className="form-select form-select-sm" value={noteForm.subject} onChange={e => nf('subject', e.target.value)}>
                      <option value="">Select</option>
                      {subjectOptions.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label" style={{ fontSize: 12 }}>Chapter</label>
                    <input className="form-control form-control-sm" value={noteForm.chapter} onChange={e => nf('chapter', e.target.value)} placeholder="e.g. Photosynthesis" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label" style={{ fontSize: 12 }}>Note Type</label>
                    <select className="form-select form-select-sm" value={noteForm.note_type} onChange={e => nf('note_type', e.target.value)}>
                      {NOTE_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label" style={{ fontSize: 12 }}>Prompt / Instructions</label>
                    <div className="input-group input-group-sm">
                      <input className="form-control" value={noteAiPrompt} onChange={e => setNoteAiPrompt(e.target.value)}
                        placeholder="e.g. Generate chapter notes with key concepts, definitions, and 5 practice questions..." />
                      <button className="btn btn-outline-secondary" type="button" onClick={() =>
                        setNoteAiPrompt(`Generate detailed ${noteForm.note_type} for ${noteForm.class_grade || 'the class'}, ${noteForm.subject || 'the subject'}, chapter: ${noteForm.chapter || 'the topic'}. Include key concepts, definitions, examples, and practice questions.`)}>
                        <i className="bi bi-magic"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <button className="btn btn-primary btn-sm w-100" onClick={generateNoteAI} disabled={noteAiGenerating || !noteAiPrompt.trim()}>
                  {noteAiGenerating ? <><span className="spinner-border spinner-border-sm me-1"></span>Generating…</> : <><i className="bi bi-robot me-1"></i>Generate Note &amp; Edit</>}
                </button>
              </div>
            </div>
          )}

          {/* Note Form */}
          {showNoteForm && (
            <div className="card mb-3" style={{ border: '2px solid #e0e7ff', borderRadius: 10 }}>
              <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#f5f8ff' }}>
                <span className="fw-semibold" style={{ fontSize: 13 }}><i className="bi bi-pencil me-2 text-primary"></i>{editingNote ? 'Edit Note' : 'Create New Note'}</span>
                <button type="button" className="btn-close btn-sm" onClick={() => { setShowNoteForm(false); setEditingNote(null); }}></button>
              </div>
              <div className="card-body">
                <form onSubmit={saveNote}>
                  <div className="row g-2">
                    <div className="col-md-5">
                      <label className="form-label" style={{ fontSize: 12 }}>Title <span className="text-danger">*</span></label>
                      <input className="form-control form-control-sm" required value={noteForm.title} onChange={e => nf('title', e.target.value)} placeholder="e.g. Chapter 5 - Photosynthesis Notes" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label" style={{ fontSize: 12 }}>Note Type</label>
                      <select className="form-select form-select-sm" value={noteForm.note_type} onChange={e => nf('note_type', e.target.value)}>
                        {NOTE_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label" style={{ fontSize: 12 }}>Tags (comma separated)</label>
                      <input className="form-control form-control-sm" placeholder="e.g. exam, revision" value={noteForm.tags} onChange={e => nf('tags', e.target.value)} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label" style={{ fontSize: 12 }}>Class</label>
                      <select className="form-select form-select-sm" value={noteForm.class_grade} onChange={e => nf('class_grade', e.target.value)}>
                        <option value="">Select</option>
                        {classOptions.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label" style={{ fontSize: 12 }}>Section</label>
                      <select className="form-select form-select-sm" value={noteForm.section} onChange={e => nf('section', e.target.value)}>
                        <option value="">All Sections</option>
                        {sectionOptions.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label" style={{ fontSize: 12 }}>Subject</label>
                      <select className="form-select form-select-sm" value={noteForm.subject} onChange={e => nf('subject', e.target.value)}>
                        <option value="">Select</option>
                        {subjectOptions.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label" style={{ fontSize: 12 }}>Chapter / Topic</label>
                      <input className="form-control form-control-sm" placeholder="Chapter / Topic" value={noteForm.chapter} onChange={e => nf('chapter', e.target.value)} />
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <label className="form-label mb-0" style={{ fontSize: 12 }}>Content <span className="text-danger">*</span></label>
                        <button type="button" className="btn btn-sm" style={{ background: '#ede9fe', color: '#6366f1', fontSize: 10, padding: '1px 8px' }}
                          onClick={() => {
                            setNoteAiPrompt(`Generate detailed ${noteForm.note_type} for ${noteForm.class_grade || 'class'}, ${noteForm.subject || 'subject'}, chapter: ${noteForm.chapter || 'topic'}.`);
                            setShowNoteAi(true); setShowNoteForm(false);
                          }}>
                          <i className="bi bi-robot me-1"></i>Generate with AI
                        </button>
                        <button type="button" className="btn btn-sm" style={{ background: '#e0f2fe', color: '#0369a1', fontSize: 10, padding: '1px 8px' }}
                          onClick={() => noteFileRef.current?.click()}>
                          <i className="bi bi-file-earmark-pdf me-1"></i>Import File
                        </button>
                        <input type="file" ref={noteFileRef} accept=".pdf,.doc,.docx,.txt" style={{ display: 'none' }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0]; if (!file) return;
                            try {
                              const fd = new FormData(); fd.append('file', file);
                              const r = await api.post('/education/notes/parse-pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                              nf('content', r.data?.text || r.data?.content || '');
                            } catch { nf('content', noteForm.content + `\n\n[Imported: ${file.name}]`); }
                          }} />
                      </div>
                      <textarea className="form-control form-control-sm" rows={12} required value={noteForm.content} onChange={e => nf('content', e.target.value)}
                        placeholder="Type or paste note content here...&#10;Supports markdown: # Heading, **bold**, - bullet points" />
                    </div>
                    <div className="col-12 d-flex gap-2 justify-content-end pt-1 border-top">
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowNoteForm(false); setEditingNote(null); }}>Cancel</button>
                      <button type="submit" className="btn btn-primary btn-sm" disabled={noteSaving}>
                        {noteSaving ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-check2 me-1"></i>}
                        {editingNote ? 'Update Note' : 'Save Note'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* View Note Detail */}
          {viewNote && (
            <div className="card mb-3" style={{ border: '2px solid #e0e7ff', borderRadius: 12 }}>
              <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#f5f3ff' }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}><i className="bi bi-journal-text me-2 text-primary"></i>{viewNote.recordData?.title}</span>
                <div className="d-flex gap-1">
                  <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => downloadNote(viewNote)} title="Download as TXT"><i className="bi bi-download"></i></button>
                  <button className={`btn btn-sm ${bookmarks.includes(viewNote._id) ? 'btn-warning' : 'btn-outline-warning'}`} style={{ fontSize: 11 }} onClick={() => toggleBookmark(viewNote._id)} title="Bookmark">
                    <i className="bi bi-bookmark-fill"></i>
                  </button>
                  <button className="btn btn-outline-success btn-sm" style={{ fontSize: 11 }} onClick={() => duplicateNote(viewNote)} title="Duplicate"><i className="bi bi-copy"></i></button>
                  {isStaff && (
                    <button className="btn btn-outline-primary btn-sm" style={{ fontSize: 11 }} onClick={() => {
                      setNoteForm({ ...blankNote, ...viewNote.recordData }); setEditingNote(viewNote);
                      setShowNoteForm(true); setViewNote(null);
                    }}><i className="bi bi-pencil me-1"></i>Edit</button>
                  )}
                  <button type="button" className="btn-close" onClick={() => setViewNote(null)}></button>
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {[
                    { label: viewNote.recordData?.class_grade, icon: 'bi-mortarboard', color: '#6366f1' },
                    { label: viewNote.recordData?.section,     icon: 'bi-grid-3x3-gap', color: '#f59e0b' },
                    { label: viewNote.recordData?.subject,     icon: 'bi-book',          color: '#22c55e' },
                    { label: viewNote.recordData?.chapter,     icon: 'bi-bookmark',      color: '#0ea5e9' },
                  ].filter(b => b.label).map((b, i) => (
                    <span key={i} className="badge" style={{ background: `${b.color}15`, color: b.color, fontSize: 12, padding: '5px 10px' }}>
                      <i className={`bi ${b.icon} me-1`}></i>{b.label}
                    </span>
                  ))}
                  <span className="badge" style={{ background: `${NOTE_TYPE_COLORS[viewNote.recordData?.note_type] || '#6366f1'}20`, color: NOTE_TYPE_COLORS[viewNote.recordData?.note_type] || '#6366f1', fontSize: 12, padding: '5px 10px' }}>
                    {viewNote.recordData?.note_type}
                  </span>
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.9, whiteSpace: 'pre-wrap', color: '#334155', background: '#f8fafc', padding: 16, borderRadius: 8, border: '1px solid #e2e8f0', maxHeight: 500, overflowY: 'auto' }}>
                  {viewNote.recordData?.content || <span className="text-muted">No content</span>}
                </div>
                {viewNote.recordData?.tags && (
                  <div className="mt-2 d-flex flex-wrap gap-1">
                    {viewNote.recordData.tags.split(',').map(tag => <span key={tag.trim()} className="badge bg-secondary bg-opacity-25 text-secondary" style={{ fontSize: 11 }}>#{tag.trim()}</span>)}
                  </div>
                )}
                {isAdmin && classOptions.filter(c => c !== viewNote.recordData?.class_grade).length > 0 && (
                  <div className="mt-3 pt-2 border-top d-flex align-items-center flex-wrap gap-1">
                    <span style={{ fontSize: 12, color: '#64748b' }}>Copy to class:</span>
                    {classOptions.filter(c => c !== viewNote.recordData?.class_grade).slice(0, 8).map(c => (
                      <button key={c} className="btn btn-outline-secondary btn-sm" style={{ fontSize: 10, padding: '1px 6px' }} onClick={() => copyToClass(viewNote, c)}>{c}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes Grid */}
          {notesLoading ? (
            <div className="text-center py-4"><span className="spinner-border text-primary"></span></div>
          ) : notes.length === 0 ? (
            <div className="card text-center py-5 text-muted">
              <i className="bi bi-journal-richtext display-4 d-block mb-2 opacity-25"></i>
              <div>No notes found.{isStaff && ' Click "New Note" or use AI to create study notes.'}</div>
            </div>
          ) : (
            <div className="row g-3">
              {notes.map(note => {
                const d           = note.recordData || {};
                const typeColor   = NOTE_TYPE_COLORS[d.note_type] || '#6366f1';
                const isBookmarked = bookmarks.includes(note._id);
                return (
                  <div className="col-md-4 col-lg-3" key={note._id}>
                    <div className="card h-100 shadow-sm" style={{ borderRadius: 12, borderTop: `3px solid ${typeColor}`, cursor: 'pointer', position: 'relative' }}
                      onClick={() => setViewNote(note)}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <span className="badge" style={{ background: typeColor + '20', color: typeColor, fontSize: 10 }}>{d.note_type || 'Notes'}</span>
                          <div className="d-flex gap-0" onClick={e => e.stopPropagation()}>
                            <button className="btn btn-sm" style={{ padding: '1px 4px', fontSize: 12, color: isBookmarked ? '#f59e0b' : '#cbd5e1', background: 'none', border: 'none' }}
                              onClick={() => toggleBookmark(note._id)} title="Bookmark">
                              <i className={`bi ${isBookmarked ? 'bi-bookmark-fill' : 'bi-bookmark'}`}></i>
                            </button>
                            {isStaff && (
                              <>
                                <button className="btn btn-sm" style={{ padding: '1px 4px', fontSize: 12, color: '#cbd5e1', background: 'none', border: 'none' }}
                                  onClick={() => duplicateNote(note)} title="Duplicate"><i className="bi bi-copy"></i></button>
                                <button className="btn btn-sm" style={{ padding: '1px 4px', fontSize: 12, color: '#cbd5e1', background: 'none', border: 'none' }}
                                  onClick={() => downloadNote(note)} title="Download"><i className="bi bi-download"></i></button>
                                <button className="btn btn-sm" style={{ padding: '1px 4px', fontSize: 12, color: '#fca5a5', background: 'none', border: 'none' }}
                                  onClick={() => deleteNote(note._id)} title="Delete"><i className="bi bi-trash"></i></button>
                              </>
                            )}
                          </div>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, lineHeight: 1.4, paddingRight: 4 }}>
                          {d.title || 'Untitled'}
                        </div>
                        <div className="d-flex flex-wrap gap-1 mb-1">
                          {d.class_grade && <span style={{ fontSize: 11, color: '#6366f1' }}><i className="bi bi-mortarboard me-1"></i>{d.class_grade}</span>}
                          {d.section     && <span style={{ fontSize: 11, color: '#f59e0b' }}>· Sec {d.section}</span>}
                          {d.subject     && <span style={{ fontSize: 11, color: '#64748b' }}>· {d.subject}</span>}
                        </div>
                        {d.chapter && <div style={{ fontSize: 12, color: '#94a3b8' }}><i className="bi bi-bookmark me-1"></i>{d.chapter}</div>}
                        {d.content && (
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {d.content.substring(0, 100)}
                          </div>
                        )}
                        {d.tags && (
                          <div className="mt-2 d-flex flex-wrap gap-1">
                            {d.tags.split(',').slice(0, 3).map(tag => <span key={tag.trim()} className="badge bg-secondary bg-opacity-25 text-secondary" style={{ fontSize: 10 }}>#{tag.trim()}</span>)}
                          </div>
                        )}
                      </div>
                      <div className="card-footer py-1" style={{ fontSize: 11, color: '#94a3b8', background: '#f8fafc' }}>
                        {new Date(note.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {noteTotal > 20 && (
            <div className="d-flex justify-content-center gap-2 mt-3">
              <button className="btn btn-outline-secondary btn-sm" disabled={notePage <= 1} onClick={() => setNotePage(p => p - 1)}>‹ Prev</button>
              <span style={{ fontSize: 13, padding: '4px 8px' }}>Page {notePage} of {Math.ceil(noteTotal / 20)}</span>
              <button className="btn btn-outline-secondary btn-sm" disabled={notePage >= Math.ceil(noteTotal / 20)} onClick={() => setNotePage(p => p + 1)}>Next ›</button>
            </div>
          )}
        </>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
